import axios from 'axios';

// FIX #24 — Import SheetJS as a proper module instead of relying on a global CDN variable.
// Run: npm install xlsx   (adds to package.json if not already present)
import * as XLSX from 'xlsx';

export const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5001').replace(/\/+$/, '');

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Surface the backend's human-readable message (e.g. "Insufficient credits...")
// instead of axios's generic "Request failed with status code 4xx". Every caller
// that does `catch (e) { showToast(e.message) }` now gets the real reason.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const backendMessage = error?.response?.data?.message;
    return Promise.reject(new Error(backendMessage || error?.message || 'Request failed'));
  }
);

// export const API_BASE_URL = import.meta.env.NODE_ENV === 'production' 
//   ? import.meta.env.VITE_API_BASE_URL 
//   : import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5001';

export function fetchMock<T>(data: T, delay: number = 600): Promise<T> {
    return new Promise((resolve) => {
        setTimeout(() => {
            const cloned = JSON.parse(JSON.stringify(data)) as T;
            resolve(cloned);
        }, delay);
    });
}


export const performScrape = async (
    options: {
        target: string;
        emailLimit: number;
        scrapeType: 'followers' | 'following';
        scrapeGoal?: 'emails' | 'profiles' | 'list';
        resultLimit?: number;
        // Canonical fields — backend normalizes them, but sending the right
        // values here keeps the wire payload tight and predictable.
        targetType?: 'username' | 'hashtag';
        // Legacy aliases (still accepted by the backend's normalizer):
        scrape_goal?: string;
        target_type?: string;
        location?: string;
    }
): Promise<{ message: string }> => {
    // Derive targetType from the target string so the frontend doesn't have
    // to compute it in two places.
    const derivedTargetType: 'username' | 'hashtag' =
        options.targetType || (options.target.trim().startsWith('#') ? 'hashtag' : 'username');

    const response = await api.post('/scrapes', {
        target: options.target,
        emailLimit: options.emailLimit,
        scrapeType: options.scrapeType,
        scrapeGoal: options.scrapeGoal || 'emails',
        resultLimit: options.resultLimit || options.emailLimit,
        targetType: derivedTargetType,
        // Send the legacy aliases too, for safety with stale backend caches.
        scrape_goal: options.scrape_goal,
        target_type: options.target_type || derivedTargetType,
        location: options.location || 'in',
    });

    return response.data.data;
};

export const downloadRawCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const exportToCSV = (data: Record<string, unknown>[], filename: string) => {
    if (data.length === 0) {
        alert("No data to export.");
        return;
    }
    const header = Object.keys(data[0]).join(',');
    const rows = data.map(row => 
        Object.values(row).map(val => {
            const str = String(val ?? '');
            return `"${str.replace(/"/g, '""')}"`;
        }).join(',')
    ).join('\n');
    
    const csvContent = `${header}\n${rows}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const exportToExcel = (data: Record<string, unknown>[], filename: string) => {
    if (data.length === 0) {
        alert("No data to export.");
        return;
    }
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Scrape Results');
    XLSX.writeFile(workbook, filename);
};

export default api;